import React, { useMemo } from "react";
import { Box, CircularProgress, Typography, useTheme } from "@mui/material";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";

type KPICardProps = {
  id?: string;
  title: string;
  value: string | number;
  valueFormat?: string;
  subtitle?: string;
  icon?: IconDefinition;
  seriesData?: (number | null)[];
  seriesType?: "spline" | "line" | "area";
  loading?: boolean;
  error?: boolean;
  errorMessage?: string;
  testId?: string;
};

export const KPICard: React.FC<KPICardProps> = ({
  id,
  title,
  value,
  valueFormat = "{value}",
  subtitle,
  icon,
  seriesData = [],
  seriesType = "area",
  loading = false,
  error = false,
  errorMessage = "Error loading data",
  testId,
}) => {
  const theme = useTheme();
  const formattedValue = valueFormat.replace("{value}", String(value));
  // Split "23.5 kWh" into { number: "23.5", unit: "kWh" } for separate typography styling
  const valueParts = useMemo(() => {
    const str = formattedValue.trim();
    let i = 0;
    while (
      i < str.length &&
      ((str[i] >= "0" && str[i] <= "9") || str[i] === "." || str[i] === "," || str[i] === "-")
    ) {
      // eslint-disable-next-line no-plusplus
      i++;
    }
    if (i === 0) return { number: str, unit: null };
    const number = str.slice(0, i);
    const unit = str.slice(i).trim() || null;
    return { number, unit };
  }, [formattedValue]);
  const hasSparkline =
    Array.isArray(seriesData) && seriesData.length > 0 && seriesData.some((val) => val != null);

  const cardBg = theme.palette.primary.main;
  const cardText = theme.palette.primary.contrastText;
  const sparklineColor = theme.palette.primary.light;

  const cardStyle = {
    backgroundColor: cardBg,
    height: 220,
    borderRadius: "4px",
    display: "flex",
    flexDirection: "column",
    p: 2,
  };

  const chartOptions: Highcharts.Options = useMemo(
    () => ({
      chart: {
        type: seriesType,
        backgroundColor: "transparent",
        margin: [2, 2, 2, 2],
        spacing: [0, 0, 0, 0],
        height: 60,
      },
      title: { text: undefined },
      credits: { enabled: false },
      legend: { enabled: false },
      xAxis: { visible: false, min: 0 },
      yAxis: { visible: false },
      tooltip: { enabled: false },
      plotOptions: {
        series: {
          animation: false,
          lineWidth: 2,
          states: { hover: { enabled: false } },
          marker: { enabled: false },
          enableMouseTracking: false,
        },
        area: {
          lineWidth: 2,
          color: sparklineColor,
          fillColor: {
            linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
            stops: [
              [0, sparklineColor],
              [1, cardBg],
            ],
          },
          threshold: null,
        },
      },
      series: [
        {
          type: seriesType,
          data: hasSparkline ? seriesData : [],
        } as Highcharts.SeriesOptionsType,
      ],
    }),
    [seriesType, sparklineColor, hasSparkline, seriesData]
  );

  if (loading) {
    return (
      <Box
        data-testid={testId}
        sx={{ ...cardStyle, justifyContent: "center", alignItems: "center" }}
      >
        <CircularProgress sx={{ color: cardText }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        data-testid={testId}
        sx={{ ...cardStyle, justifyContent: "center", alignItems: "center" }}
      >
        <Typography color="error">{errorMessage}</Typography>
      </Box>
    );
  }

  return (
    <Box id={id} data-testid={testId} sx={{ ...cardStyle, position: "relative" }}>
      <Typography
        variant="titleSmall"
        sx={{
          color: cardText,
          position: "absolute",
          top: 16,
          left: 16,
          right: 16,
        }}
      >
        {title}
      </Typography>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          pt: 4,
          mb: hasSparkline ? -6 : 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "center" }}>
          {icon && (
            <Box
              sx={{
                mr: 2.5,
                fontSize: "3rem",
                color: cardText,
                display: "flex",
                alignSelf: "center",
              }}
            >
              <FontAwesomeIcon icon={icon} />
            </Box>
          )}
          <Typography variant="displayLarge" sx={{ color: cardText, lineHeight: 1 }}>
            {valueParts.number}
          </Typography>
          {valueParts.unit && (
            <Typography variant="displaySmall" sx={{ color: cardText, lineHeight: 1, ml: 1 }}>
              {valueParts.unit}
            </Typography>
          )}
        </Box>

        {subtitle && (
          <Typography variant="bodyMedium" sx={{ color: cardText, textAlign: "center", mt: 1 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {hasSparkline && (
        <Box sx={{ height: 60, width: "100%", position: "relative" }}>
          <HighchartsReact highcharts={Highcharts} options={chartOptions} />
        </Box>
      )}
    </Box>
  );
};

export default KPICard;
